import React from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { HiCheckCircle } from 'react-icons/hi';
import dayjs from 'dayjs';

import FormDate from 'partial/form/FormDate';
import FormInput from 'partial/form/FormInput';
import Button from 'partial/components/Button';
import FormDatasetOptions from 'core-modules/dataset/components/FormDatasetOptions';
import Form, {
  ErrorLabel,
  ErrorOrDescription,
  setError422,
} from 'partial/form/Form';
import FormFileDnd from 'partial/form/FormFileDnd';
import { objectToString } from 'helper';

import {
  useSaveBusinessGovernment,
  useBusinessGovernment,
  useFormBusinessGovernment,
} from '../hooks';
import { transformBusinessGovernment } from '../transformers';

const initialValues = transformBusinessGovernment({});

const validationSchema = yup.object().shape({
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
  district: yup.string().required('Required'),
  street: yup.string(),
  house_no: yup.string(),
  building_name: yup.string(),
  unit_no: yup.string(),
  postal_code: yup
    .string()
    .required('Required')
    .min(6, 'At least 6 characters')
    .max(6, 'Maximum Postal Code is at 6 characters')
    .matches(/^[A-Za-z0-9]+$/, 'Invalid format'),
  owner_id_image_url: yup.array().min(1, 'Required'),
  government_ministry: yup.string().required('Required'),
  date_of_government_official_letter: yup
    .string()
    .required('Required')
    .test(
      'business_incorporation_date',
      'Date must be current or in the past',
      (value) => {
        return dayjs().isSame(value, 'date') || dayjs().isAfter(value, 'date');
      }
    ),
  government_official_letter_refno: yup.string().required('Required'),
  authorised_name_of_contact_person: yup.string().required('Required'),
  authorised_contact_person_ic_no: yup.string().required('Required'),
  ministry_appointment_letter_img_url: yup.array().min(1, 'Required'),
  ic_authorised_person_img_url: yup.array().min(2, 'Minimum of 2 images'),
  bank_name: yup.string().required('Required'),
  bank_account_name: yup.string().required('Required'),
  bank_account_number: yup.string().required('Required'),
});

const handleKeyPress = (e) => {
  if (e.charCode >= 48 && e.charCode <= 57) return;
  e.preventDefault();
};

function BusinessGovernment({ onNext }) {
  const [isLoading, submit] = useSaveBusinessGovernment();
  const [validateOnChange, setValidateOnChange] = React.useState(false);
  const [, data] = useBusinessGovernment();
  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    setValues,
    setFieldError: onSetFieldError,
  } = useFormik({
    initialValues: { ...initialValues, ...data },
    validationSchema,
    onSubmit: async (form, { setFieldError }) => {
      const param = {
        ...form,
        owner_id_image_url: objectToString(form?.owner_id_image_url),
        ministry_appointment_letter_img_url: objectToString(
          form?.ministry_appointment_letter_img_url
        ),
        ministry_letter_for_appointed_img_url: objectToString(
          form?.ministry_letter_for_appointed_img_url
        ),
        ic_authorised_person_img_url: objectToString(
          form?.ic_authorised_person_img_url
        ),
      };
      submit(param, onNext, (err) => {
        setError422(err, setFieldError);
      });
    },
    validateOnChange,
  });
  useFormBusinessGovernment(setValues);

  return (
    <Form
      className="space-y-4 max-w-3xl mx-auto"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
      error={errors}
    >
      <div className="space-y-3 mb-5">
        <div className="title-h1 text-center">
          Government - Business Information
        </div>
        <p className="text-center text-base text-gray-800">
          Details about your business like bank details, business documents and
          others
        </p>
      </div>
      <hr />
      <div className="space-y-3">
        <div>
          <div className="title-h2">Representative&apos;s Information</div>
          <div className="text-base text-gray-800">
            We need some information about the representative answering this
            form.
          </div>
        </div>
        <div>
          <div className="text-sm font-semibold">Full Legal Name</div>
          <div className="text-sm text-gray-500">
            This is the name that appears in any of your government-issued IDs.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 relative z-20">
          <div>
            <FormInput
              name="first_name"
              onSetFieldValue={setFieldValue}
              value={values?.first_name}
              error={errors?.first_name}
              label="First Name"
              placeholder="Enter First Name"
            />
            <ErrorLabel name="first_name" />
          </div>
          <div>
            <FormInput
              name="last_name"
              onSetFieldValue={setFieldValue}
              value={values?.last_name}
              error={errors?.last_name}
              label="Last Name"
              placeholder="Enter Last Name"
            />
            <ErrorLabel name="last_name" />
          </div>
        </div>
        <div className="text-sm font-semibold">Home Address</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FormDatasetOptions
              code="DISTRICT"
              name="district"
              onSetFieldValue={setFieldValue}
              value={values?.district}
              error={errors?.district}
              label="District/Mukim/Village"
            />
            <ErrorLabel name="district" />
          </div>
          <div>
            <FormInput
              name="street"
              onSetFieldValue={setFieldValue}
              value={values?.street}
              error={errors?.street}
              optional
              label="Street/Simpang"
              placeholder="Enter Street/ Simpang"
            />
            <ErrorLabel name="street" />
          </div>
          <div>
            <FormInput
              name="house_no"
              onSetFieldValue={setFieldValue}
              value={values?.house_no}
              error={errors?.house_no}
              optional
              label="Block/House No."
              placeholder="Enter Block/ House No."
            />
            <ErrorLabel name="house_no" />
          </div>
          <div>
            <FormInput
              name="building_name"
              onSetFieldValue={setFieldValue}
              value={values?.building_name}
              error={errors?.building_name}
              optional
              label="Building Name"
              placeholder="Enter Building Name"
            />
            <ErrorLabel name="building_name" />
          </div>
          <div>
            <FormInput
              name="unit_no"
              onSetFieldValue={setFieldValue}
              value={values?.unit_no}
              error={errors?.unit_no}
              optional
              label="Floor/Unit No."
              placeholder="Enter Floor/ Unit No."
            />
            <ErrorLabel name="unit_no" />
          </div>
          <div>
            <FormInput
              name="postal_code"
              onSetFieldValue={setFieldValue}
              value={values?.postal_code}
              error={errors?.postal_code}
              label="Postal Code"
              placeholder="Enter Postal Code"
            />
            <ErrorLabel name="postal_code" />
          </div>
        </div>
        <div className="h-1 bg-gray-200" />
        <div className="flex flex-col space-y-3">
          <div className="title-h2">Upload ID</div>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 space-y-1">
            <div className="text-gray-500 font-semibold">
              Guidelines for uploading IDs
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">Do not submit expired IDs.</p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Make sure all information is legible. Do not censor, black out
                or redact any data.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Do not crop the ID. All corners of the ID should be visible
                against the backdrop.
              </p>
            </div>
          </div>
          <div>
            <FormFileDnd
              className="h-[176px]"
              name="owner_id_image_url"
              onSetFieldValue={setFieldValue}
              onSetFieldError={onSetFieldError}
              value={values?.owner_id_image_url}
              error={errors?.owner_id_image_url}
              rightAlign
              label="Valid identification documents"
              description={
                <div className="flex flex-col space-y-4">
                  <p className="text-gray-500">
                    Upload your Brunei Identification Card (front and back).
                  </p>
                  <p className="text-gray-500">
                    Accepted files are .jpg, .png or .pdf with a file size lower
                    than 10MB.
                  </p>
                </div>
              }
              max={10}
            />
            <ErrorLabel name="owner_id_image_url" />
          </div>
        </div>
        <div className="h-1 bg-gray-200" />
        <div className="title-h2">Government Details</div>
        <div>
          <FormInput
            name="government_ministry"
            onSetFieldValue={setFieldValue}
            value={values?.government_ministry}
            error={errors?.government_ministry}
            label="Government Ministry"
            placeholder="Enter Government Ministry"
          />
          <ErrorLabel name="government_ministry" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FormDate
              name="date_of_government_official_letter"
              onSetFieldValue={setFieldValue}
              placeholder="DD/MM/YYYY"
              value={values?.date_of_government_official_letter}
              error={errors?.date_of_government_official_letter}
              label="Date of Government Official Letter"
              maxDate={Date.now()}
            />
            <ErrorLabel name="date_of_government_official_letter" />
          </div>
          <div>
            <FormInput
              name="government_official_letter_refno"
              onSetFieldValue={setFieldValue}
              value={values?.government_official_letter_refno}
              error={errors?.government_official_letter_refno}
              label="Government Official Letter (Reference No.)"
              placeholder="Enter Government Official Letter (Reference No.)"
            />
            <ErrorLabel name="government_official_letter_refno" />
          </div>
          <div>
            <FormInput
              name="authorised_name_of_contact_person"
              onSetFieldValue={setFieldValue}
              value={values?.authorised_name_of_contact_person}
              error={errors?.authorised_name_of_contact_person}
              label="Authorised Name of Contact Person"
              placeholder="Enter Authorised Name of Contact Person"
            />
            <ErrorOrDescription
              name="authorised_name_of_contact_person"
              label="Person given the domain rights"
            />
          </div>
          <div>
            <FormInput
              name="authorised_contact_person_ic_no"
              onSetFieldValue={setFieldValue}
              value={values?.authorised_contact_person_ic_no}
              error={errors?.authorised_contact_person_ic_no}
              label="Authorised Contact Person IC Number"
              placeholder="Enter Authorised Contact Person IC Number"
            />
            <ErrorLabel name="authorised_contact_person_ic_no" />
          </div>
        </div>

        <div className="h-1 bg-gray-200" />
        <div className="flex flex-col space-y-3">
          <div>
            <div className="title-h2">Upload documents</div>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 space-y-1">
            <div className="text-gray-500 font-semibold">
              Guidelines for uploading of documents
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                The documents must be in clear copies and in color.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Make sure the documents have complete pages.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                The documents must not be redacted (all information is visible
                and data is legible).
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                All corners of the documents are visible against the backdrop.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Accepted files are .jpg, .png or .pdf with a file size lower
                than 10MB.
              </p>
            </div>
          </div>
          <div>
            <FormFileDnd
              className="h-[200px]"
              name="ministry_appointment_letter_img_url"
              onSetFieldValue={setFieldValue}
              onSetFieldError={onSetFieldError}
              value={values?.ministry_appointment_letter_img_url}
              error={errors?.ministry_appointment_letter_img_url}
              label="Ministry Appointment Letter"
              rightAlign
              max={10}
            />
            <ErrorLabel name="ministry_appointment_letter_img_url" />
          </div>
          <div>
            <FormFileDnd
              className="h-[200px]"
              name="ministry_letter_for_appointed_img_url"
              onSetFieldValue={setFieldValue}
              onSetFieldError={onSetFieldError}
              value={values?.ministry_letter_for_appointed_img_url}
              error={errors?.ministry_letter_for_appointed_img_url}
              label="Ministry Letter for Appointed Authorised Person"
              rightAlign
              max={10}
            />
            <ErrorLabel name="ministry_appointment_letter_img_url" />
          </div>
          <div>
            <FormFileDnd
              className="h-[200px]"
              name="ic_authorised_person_img_url"
              onSetFieldValue={setFieldValue}
              onSetFieldError={onSetFieldError}
              value={values?.ic_authorised_person_img_url}
              error={errors?.ic_authorised_person_img_url}
              label="IC Authorised Person"
              imagesOnly
              description={
                <div className="flex flex-col space-y-4">
                  <p className="text-gray-500">
                    Upload Identity Card (front and back).
                  </p>
                  <p className="text-gray-500">
                    Accepted files are .jpg or .png with a file size lower than
                    10MB.
                  </p>
                </div>
              }
              max={10}
              rightAlign
            />
            <ErrorLabel name="ic_authorised_person_img_url" />
          </div>
        </div>
        <div className="h-1 bg-gray-200" />
        <div className="title-h2">Bank Details</div>
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 space-y-1">
          <p className="text-gray-500">
            Bank account should be under the name of the registered business.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <FormDatasetOptions
              code="BANKS"
              name="bank_name"
              onSetFieldValue={setFieldValue}
              value={values?.bank_name}
              error={errors?.bank_name}
              label="Bank Name"
            />
            <ErrorLabel name="bank_name" />
          </div>
          <div className="col-span-1">
            <FormInput
              name="bank_account_name"
              onSetFieldValue={setFieldValue}
              value={values?.bank_account_name}
              error={errors?.bank_account_name}
              label="Account Name"
              placeholder="Enter Account Name"
            />
            <ErrorOrDescription
              name="bank_account_name"
              label="This should be under the name of the registered business."
            />
          </div>
          <div className="col-span-1">
            <FormInput
              name="bank_account_number"
              onSetFieldValue={setFieldValue}
              value={values?.bank_account_number}
              error={errors?.bank_account_number}
              label="Account Number"
              onKeyPress={handleKeyPress}
              placeholder="Enter Account Number"
            />
            <ErrorOrDescription
              name="bank_account_number"
              label="This should be a Brunei dollar savings or current account."
            />
          </div>
        </div>
      </div>
      <div className="mt-3 border-t pt-3 space-x-2 flex justify-end">
        <Button
          primary
          size="lg"
          type="submit"
          disabled={isLoading}
          onClick={() => setValidateOnChange(true)}
        >
          Continue to Statement of Acceptance
        </Button>
      </div>
    </Form>
  );
}

BusinessGovernment.propTypes = {
  onNext: PropTypes.func.isRequired,
  // onBack: PropTypes.func.isRequired,
};

export default BusinessGovernment;
